import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@shared/ui';
import type { AddressControlMode, WalletMonitoringAddress } from '@shared/api/contracts';
import {
  CONTROL_MODES,
  CONTROL_MODE_LABEL_KEYS,
  isControlReasonRequired,
  MAX_CONTROL_REASON_LENGTH,
  validateControlModeForm,
} from '@entities/wallets';

interface ControlModeModalProps {
  open: boolean;
  address: WalletMonitoringAddress | null;
  isSubmitting?: boolean;
  serverError?: string | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: { mode: AddressControlMode; reason: string }) => void;
}

export function ControlModeModal({
  open,
  address,
  isSubmitting = false,
  serverError,
  onOpenChange,
  onSubmit,
}: ControlModeModalProps) {
  const { t } = useTranslation('cabinet');
  const [mode, setMode] = useState<AddressControlMode>('normal');
  const [reason, setReason] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !address) return;
    setMode(address.controlMode);
    setReason(address.controlReason ?? '');
    setFieldError(null);
  }, [open, address]);

  const handleSubmit = () => {
    const validation = validateControlModeForm(mode, reason);
    if (validation === 'required') {
      setFieldError(t('wallets.controlMode.reasonRequired'));
      return;
    }
    if (validation === 'maxLength') {
      setFieldError(t('wallets.controlMode.reasonMaxLength', { max: MAX_CONTROL_REASON_LENGTH }));
      return;
    }

    setFieldError(null);
    onSubmit({ mode, reason });
  };

  if (!address) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('wallets.controlMode.title')}</DialogTitle>
          <DialogDescription>{t('wallets.controlMode.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t('wallets.controlMode.modeLabel')}</Label>
            <Select value={mode} onValueChange={(value) => setMode(value as AddressControlMode)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTROL_MODES.map((item) => (
                  <SelectItem key={item} value={item}>
                    {t(CONTROL_MODE_LABEL_KEYS[item])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isControlReasonRequired(mode) && (
            <div className="space-y-2">
              <Label>{t('wallets.controlMode.reasonLabel')}</Label>
              <Textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                maxLength={MAX_CONTROL_REASON_LENGTH}
                rows={4}
                placeholder={t('wallets.controlMode.reasonPlaceholder')}
              />
              {fieldError && <p className="text-sm text-destructive">{fieldError}</p>}
            </div>
          )}

          {serverError && <p className="text-sm text-destructive">{serverError}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('common.cancel', { ns: 'common' })}
            </Button>
            <Button type="button" variant="default" onClick={handleSubmit} loading={isSubmitting}>
              {t('wallets.controlMode.submit')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
